import { Dispatch } from 'redux';
import { clearFields } from 'redux-form';
import {
    CannedQuestionName,
    questionCustomisationFilterPrefix,
    QuestionCustomisationFilterType,
} from '../../api/models/survey';
import {
    FormAnswer,
    isInventoryQuestion,
    isTaxQuestion,
    SurveyQuestion,
} from '../../models/survey';

const hasRequiredChildOrParentOrSibling = ({
    questions,
    currentQuestionName,
    currentQuestionParentName,
}: {
    questions: any[];
    currentQuestionName: string;
    currentQuestionParentName: string | undefined;
}) =>
    questions.some(
        (question) =>
            (question.name === currentQuestionParentName ||
                question.renderConditionTrigger?.parentQuestionId ===
                    currentQuestionName ||
                (currentQuestionParentName
                    ? question.renderConditionTrigger?.parentQuestionId ===
                      currentQuestionParentName
                    : false)) &&
            question.required,
    );

export const isRequiredQuestion = (
    question: SurveyQuestion,
    questions: SurveyQuestion[],
) =>
    question.required ||
    isInventoryQuestion(question.choices) ||
    isTaxQuestion(question.name as CannedQuestionName) ||
    hasRequiredChildOrParentOrSibling({
        questions,
        currentQuestionName: question.name,
        currentQuestionParentName:
            question.renderConditionTrigger?.parentQuestionId,
    });

export const clearSurveyField =
    (formName: string, field: string) => (dispatch: Dispatch<any>) =>
        dispatch(clearFields(formName, true, true, field));

/**
 * Attendee IDs in LOS are numbers prefixed with `A-` while in NOS are integer numbers
 */
export const isAttendeeField = (field: string): boolean => {
    return field.indexOf('A-') === 0 || /^[0-9]+$/.test(field);
};

export const isCannedQuestion = (question: Pick<SurveyQuestion, 'name'>) =>
    question.name.startsWith(
        questionCustomisationFilterPrefix[
            QuestionCustomisationFilterType.canned
        ],
    );

export const isCustomQuestion = (question: Pick<SurveyQuestion, 'name'>) =>
    question.name.startsWith(
        questionCustomisationFilterPrefix[
            QuestionCustomisationFilterType.custom
        ],
    );

export const areMultipleResponsesSelected = (response: string) =>
    !!response?.includes('|');

export const getMultipleChoicesSelected = ({
    answer,
    question,
}: {
    answer: string;
    question: SurveyQuestion;
}) => {
    const existingChoices = Object.fromEntries(
        question.choices.map((choice) => [
            choice.answer.text.toString(),
            choice,
        ]),
    );
    return answer.split('|').flatMap((choiceSelected) => {
        const choice = existingChoices[choiceSelected.trim()];
        return choice ? [choice.id] : [];
    });
};

export const transformAddressFromApi = (formAddress: Record<string, string>) =>
    ({
        address1: formAddress.address_1,
        address2: formAddress.address_2,
        city: formAddress.city,
        country: formAddress.country,
        postal: formAddress.postal_code,
        region: formAddress.region,
    } as Record<string, string>);

export const transformAddressForApi = (formAddress: {
    address1: string;
    address2: string;
    city: string;
    region: string;
    country: string;
    postal: string;
}) => ({
    address_1: formAddress.address1,
    address_2: formAddress.address2,
    city: formAddress.city,
    region: formAddress.region,
    country: formAddress.country,
    postal_code: formAddress.postal,
});

export const getChoiceIdByText = ({
    answer,
    question,
}: {
    answer: FormAnswer;
    question: SurveyQuestion;
}) =>
    question.choices.find((choice) => choice.answer.text.toString() === answer)
        ?.id;
